//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import Icon from '@components/atoms/icon/Icon';
import Text from '@components/atoms/text/Text';
import TextInput from '@components/atoms/text-input/TextInput';
import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import { KEYS } from '@constants/keyboard';
import { useOutsideClick } from '@hooks/useOutsideClick';
import { Mixins, TextMixin } from '@theme/mixins';
import { keyEquals } from '@utils/keyboardUtils';
import { isNullOrEmpty } from '@utils/stringUtils';
import { ITrackableComponent, withTracking } from '@utils/withTracking';

interface EditableNameProps
  extends React.HTMLAttributes<HTMLDivElement>,
    ITrackableComponent {
  name: string;
  isDisabled?: boolean;
  disabledTooltip?: string;
  placeholder?: string;
  placement?: React.ComponentProps<typeof SmallTooltip>['placement'];
  renameTooltip?: string;
  showEditIcon?: boolean;
  onSaveName: (name: string) => Promise<void>;
  maxLength?: number;
  centerText?: boolean;
  textMixin?: TextMixin;
}

/**
 * The EditableName atom renders an arbitrary string of text like
 * a gallery or collection name, and allows the user to edit the
 * that name in place. When clicked, we'll render an input in place
 * of the name, and when the user clicks outside the input or presses
 * enter we'll save the change and render the new name.
 *
 * This component uses our standard form sizing like other inputs and buttons
 * but is built to be used in the PageHeader component.
 *
 * @param isDisabled If true, doesn't allow the user to edit the name. Useful for permissions
 * @param name The underlying name that is editable
 * @param onSaveName A function that is executed when the user tries to save the name entered in
 *   the input. If an error occurs while saving, the function should use its own error handling. It
 *   should update the name prop as part of its execution.
 * @param placement Placement of tooltip
 * @param renameTooltip If defined, will be used in the tooltip that appears on hover
 * @param showEditIcon If true, renders an edit icon next to the editable text
 * @param disabledTooltip If disabled, render this tooltip on hover
 * @param maxLength The maximum length of the name. Defaults to 50 characters.
 * @param centerText If true, will center text within the input horizontally. Useful to contexts
 *   in which this component is meant to be horizontally centered.
 * @param textMixin A value from the Mixins.Text object, used to style the text. Defaults to Section.
 *
 * @example
 * const theName = useTypedSelector(...);
 * const saveTheName = async () => { ... };
 *
 * <EditableName
 *   name={ theName }
 *   onSaveName={ (name) => saveTheName(name) }
 *   renameTooltip={ `Click to rename ${name}` }
 *   showEditIcon={ false }
 * />
 */
const EditableName = ({
  centerText = false,
  disabledTooltip = `You don't have permission to rename this.`,
  isDisabled = false,
  maxLength = 50,
  name,
  onSaveName,
  placeholder = 'Enter a name',
  placement,
  renameTooltip,
  showEditIcon = false,
  textMixin = Mixins.Text.Section,
  trackingId,
}: EditableNameProps) => {
  const [isContainerHovered, setIsContainerHovered] = useState(false);
  const [draftName, setDraftName] = useState<string>(name);
  const [
    { elementRef: nameContainerRef, isActive: isEditingName },
    setIsEditingName,
  ] = useOutsideClick<HTMLDivElement>(() => {
    saveDraftName();
  });

  // Whenever the name prop is changed, update the draft value
  useEffect(() => setDraftName(name), [name]);

  const openNameEditField = () => {
    setDraftName(name);
    setIsEditingName(true);
  };

  const saveDraftName = () => {
    // If the name is unchanged, skip the save call and delete the draft
    if (isNullOrEmpty(draftName) || draftName === name) {
      setDraftName('');
      return;
    } else {
      onSaveName(draftName)
        .then(() => {
          setIsEditingName(false);
        })
        .finally(() => {
          setDraftName('');
        });
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent) => {
    if (keyEquals(e, KEYS.ENTER)) {
      setIsEditingName(false);
      saveDraftName();
    } else if (keyEquals(e, KEYS.ESC)) {
      setIsEditingName(false);
      setDraftName('');
    }
  };

  const onMouseEnter = () => setIsContainerHovered(true);

  const onMouseLeave = () => setIsContainerHovered(false);

  return (
    <Container
      ref={nameContainerRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      trackingId={trackingId}
    >
      {isEditingName ? (
        <NameInput
          autoFocus={true}
          centerText={centerText}
          maxLength={maxLength}
          onChange={setDraftName}
          onKeyUp={handleKeyUp}
          placeholder={placeholder}
          size={draftName.length + 1}
          textMixin={textMixin}
          type='text'
          value={draftName}
        />
      ) : (
        <SmallTooltip
          content={
            isDisabled ? (
              <TooltipContent>{disabledTooltip}</TooltipContent>
            ) : (
              <TooltipContent>{renameTooltip}</TooltipContent>
            )
          }
          interactive={false}
          isDisabled={!renameTooltip}
          placement={placement}
          target={
            <NameContainer
              onClick={() => {
                if (!isDisabled) {
                  openNameEditField();
                }
              }}
            >
              <Text ellipsize={1} height={32} mixin={textMixin} offset={0}>
                {/* Render the draft name here if it exists, prevents flashing on save */}
                {draftName || name}
              </Text>
              {showEditIcon ? (
                <EditIcon
                  $isDisabled={isDisabled}
                  $isVisible={isContainerHovered}
                />
              ) : null}
            </NameContainer>
          }
        />
      )}
    </Container>
  );
};

const Container = withTracking('div');

const EditIcon = styled(Icon).attrs({
  name: 'edit',
})<{ $isVisible: boolean; $isDisabled: boolean }>(
  ({ $isDisabled, $isVisible, theme }) => `
  margin-left: ${theme.spacings[2]};
  /* Rely on visibility so that the icon always occupies space even if it's hidden */
  visibility: ${$isVisible ? 'visible' : 'hidden'};
  color: ${$isDisabled ? theme.colors.gray4 : theme.colors.gray9};
`,
);

const NameContainer = styled.div(
  ({ theme }) => `
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: ${theme.borderRadii[1]};
  padding: 0 ${theme.spacings[1]};
  &:hover {
    box-shadow: inset 0 0 0 1px ${theme.colors.gray5};
  }
`,
);

const NameInput = styled(TextInput)<{
  centerText: boolean;
  textMixin: string;
}>(
  ({ centerText, textMixin, theme }) => `
  ${textMixin}
  text-align: ${centerText ? 'center' : 'left'};
  input {
    font-family: inherit;
    min-width: 200px;
    /* When we build a new Input atom, this will be the default */
    height: ${theme.spacings[5]};
    /* Add 2px on the bottom to align the input's text with the default text */
    padding: 0 ${theme.spacings[1]} 2px calc(${theme.spacings[1]} - 1px);
    /* Shave off 1px on the left to account for the input's outline unless the text is centered */
    padding-left: ${
      centerText ? theme.spacings[1] : `calc(${theme.spacings[1]} - 1px);`
    }
    width: 100%;
    box-sizing: border-box;
  }
`,
);

const TooltipContent = styled.div`
  word-wrap: break-word;
`;

export default EditableName;
